import React, { useState, useEffect, useReducer } from "react";
import openSocket from "../../services/socket-io";

import {
  Button,
  IconButton,
  makeStyles,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Switch,
  Chip,
  Tooltip,
} from "@material-ui/core";
import {
  Edit,
  DeleteOutline,
  Schedule,
  KeyboardArrowUp,
  KeyboardArrowDown,
} from "@material-ui/icons";

import MainContainer from "../../components/MainContainer";
import MainHeader from "../../components/MainHeader";
import MainHeaderButtonsWrapper from "../../components/MainHeaderButtonsWrapper";
import Title from "../../components/Title";

import api from "../../services/api";
import { i18n } from "../../translate/i18n";
import TableRowSkeleton from "../../components/TableRowSkeleton";
import AutoReplyFlowModal from "../../components/AutoReplyFlowModal";
import ConfirmationModal from "../../components/ConfirmationModal";
import { toast } from "react-toastify";
import toastError from "../../errors/toastError";

// Una automatización "avanzada" es, para el backend, la misma
// AutoReplyRule de siempre: se distingue únicamente por tener 1+ pasos en
// vez de una quickAnswer única. Esta pantalla filtra esa misma lista.
const isFlow = (rule) => (rule.steps || []).length > 0;

const reducer = (state, action) => {
  if (action.type === "LOAD_RULES") {
    const rules = action.payload.filter(isFlow);
    const newRules = [];

    rules.forEach((rule) => {
      const ruleIndex = state.findIndex((r) => r.id === rule.id);
      if (ruleIndex !== -1) {
        state[ruleIndex] = rule;
      } else {
        newRules.push(rule);
      }
    });

    return [...state, ...newRules];
  }

  if (action.type === "UPDATE_RULES") {
    const rule = action.payload;
    const ruleIndex = state.findIndex((r) => r.id === rule.id);

    if (!isFlow(rule)) {
      if (ruleIndex !== -1) {
        state.splice(ruleIndex, 1);
        return [...state];
      }
      return state;
    }

    if (ruleIndex !== -1) {
      state[ruleIndex] = rule;
      return [...state];
    } else {
      return [rule, ...state];
    }
  }

  if (action.type === "DELETE_RULES") {
    const ruleId = action.payload;

    const ruleIndex = state.findIndex((r) => r.id === ruleId);
    if (ruleIndex !== -1) {
      state.splice(ruleIndex, 1);
    }
    return [...state];
  }

  if (action.type === "RESET") {
    return [];
  }

  if (action.type === "SET_ORDER") {
    return action.payload.filter(isFlow);
  }
};

const useStyles = makeStyles((theme) => ({
  mainPaper: {
    flex: 1,
    padding: theme.spacing(1),
    overflowY: "scroll",
    ...theme.scrollbarStyles,
  },
}));

const STEP_TYPE_LABEL = {
  text: "autoReplyFlowModal.form.steps.typeText",
  image: "autoReplyFlowModal.form.steps.typeImage",
  video: "autoReplyFlowModal.form.steps.typeVideo",
  audio: "autoReplyFlowModal.form.steps.typeAudio",
  document: "autoReplyFlowModal.form.steps.typeDocument",
};

const AutoReplyFlows = () => {
  const classes = useStyles();

  const [loading, setLoading] = useState(false);
  const [rules, dispatch] = useReducer(reducer, []);
  const [selectedRule, setSelectedRule] = useState(null);
  const [ruleModalOpen, setRuleModalOpen] = useState(false);
  const [deletingRule, setDeletingRule] = useState(null);
  const [confirmModalOpen, setConfirmModalOpen] = useState(false);
  const [reordering, setReordering] = useState(false);

  useEffect(() => {
    setLoading(true);
    const fetchRules = async () => {
      try {
        const { data } = await api.get("/autoReplyRules");
        dispatch({ type: "LOAD_RULES", payload: data });
        setLoading(false);
      } catch (err) {
        toastError(err);
        setLoading(false);
      }
    };
    dispatch({ type: "RESET" });
    fetchRules();
  }, []);

  useEffect(() => {
    const socket = openSocket();

    socket.on("autoReplyRule", (data) => {
      if (data.action === "update" || data.action === "create") {
        dispatch({ type: "UPDATE_RULES", payload: data.rule });
      }

      if (data.action === "delete") {
        dispatch({ type: "DELETE_RULES", payload: +data.ruleId });
      }

      if (data.action === "reorder") {
        dispatch({ type: "SET_ORDER", payload: data.rules });
      }
    });

    return () => {
      socket.disconnect();
    };
  }, []);

  const handleOpenRuleModal = () => {
    setSelectedRule(null);
    setRuleModalOpen(true);
  };

  const handleCloseRuleModal = () => {
    setSelectedRule(null);
    setRuleModalOpen(false);
  };

  const handleEditRule = (rule) => {
    setSelectedRule(rule.id);
    setRuleModalOpen(true);
  };

  const handleDeleteRule = async (ruleId) => {
    try {
      await api.delete(`/autoReplyRules/${ruleId}`);
      toast.success(i18n.t("autoReplyFlows.toasts.deleted"));
    } catch (err) {
      toastError(err);
    }
    setDeletingRule(null);
  };

  const handleToggleActive = async (rule) => {
    try {
      const { data } = await api.put(`/autoReplyRules/${rule.id}`, {
        active: !rule.active,
      });
      dispatch({ type: "UPDATE_RULES", payload: data });
    } catch (err) {
      toastError(err);
    }
  };

  // Igual que en Automatizaciones: mover reescribe priority (0..N) para
  // todas las filas mostradas — la misma columna que decide cuál regla
  // gana si dos llegan a coincidir.
  const handleMoveRule = async (index, direction) => {
    const targetIndex = index + direction;
    if (targetIndex < 0 || targetIndex >= rules.length || reordering) return;

    const reordered = [...rules];
    [reordered[index], reordered[targetIndex]] = [
      reordered[targetIndex],
      reordered[index],
    ];

    dispatch({ type: "SET_ORDER", payload: reordered });
    setReordering(true);
    try {
      const { data } = await api.put("/autoReplyRules/reorder", {
        orderedIds: reordered.map((r) => r.id),
      });
      dispatch({ type: "SET_ORDER", payload: data });
    } catch (err) {
      toastError(err);
      dispatch({ type: "SET_ORDER", payload: rules });
    } finally {
      setReordering(false);
    }
  };

  return (
    <MainContainer>
      <ConfirmationModal
        title={
          deletingRule &&
          `${i18n.t("autoReplyFlows.confirmationModal.deleteTitle")} ${
            deletingRule.name
          }?`
        }
        open={confirmModalOpen}
        onClose={setConfirmModalOpen}
        onConfirm={() => handleDeleteRule(deletingRule.id)}
      >
        {i18n.t("autoReplyFlows.confirmationModal.deleteMessage")}
      </ConfirmationModal>
      <AutoReplyFlowModal
        open={ruleModalOpen}
        onClose={handleCloseRuleModal}
        ruleId={selectedRule}
      />
      <MainHeader>
        <Title>{i18n.t("autoReplyFlows.title")}</Title>
        <MainHeaderButtonsWrapper>
          <Button
            variant="contained"
            color="primary"
            onClick={handleOpenRuleModal}
          >
            {i18n.t("autoReplyFlows.buttons.add")}
          </Button>
        </MainHeaderButtonsWrapper>
      </MainHeader>
      <Paper className={classes.mainPaper} variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell align="center">
                {i18n.t("autoReplyFlows.table.name")}
              </TableCell>
              <TableCell align="center">
                {i18n.t("autoReplyFlows.table.whatsapp")}
              </TableCell>
              <TableCell align="center">
                {i18n.t("autoReplyFlows.table.keywords")}
              </TableCell>
              <TableCell align="center">
                {i18n.t("autoReplyFlows.table.steps")}
              </TableCell>
              <TableCell align="center">
                {i18n.t("autoReplyFlows.table.active")}
              </TableCell>
              <TableCell align="center">
                {i18n.t("autoReplyFlows.table.actions")}
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            <>
              {rules.map((rule, index) => (
                <TableRow key={rule.id}>
                  <TableCell align="center">
                    {rule.name}
                    {rule.windows?.length > 0 && (
                      <Tooltip title={i18n.t("autoReplyFlowModal.form.windows.title")}>
                        <Schedule
                          fontSize="small"
                          color="primary"
                          style={{
                            verticalAlign: "middle",
                            marginLeft: 4,
                          }}
                        />
                      </Tooltip>
                    )}
                    {rule.parentRule && (
                      <div style={{ fontSize: 11, color: "#888" }}>
                        ↳ {i18n.t("autoReplyFlows.table.dependsOn")}:{" "}
                        {rule.parentRule.name}
                      </div>
                    )}
                    {rule.fireAfterAssigned && (
                      <div style={{ fontSize: 11, color: "#888" }}>
                        ⚡ Dispara con agente ya asignado
                      </div>
                    )}
                    {rule.userScope === "some" && (
                      <div style={{ fontSize: 11, color: "#888" }}>
                        👤{" "}
                        {rule.users && rule.users.length > 0
                          ? rule.users.map((u) => u.name).join(", ")
                          : "ningún agente"}
                      </div>
                    )}
                  </TableCell>
                  <TableCell align="center">{rule.whatsapp?.name}</TableCell>
                  <TableCell align="center">
                    {rule.matchType === "any" ? (
                      <Chip
                        label={i18n.t("autoReplyFlows.table.matchTypeAny")}
                        size="small"
                        color="primary"
                        style={{ margin: 2 }}
                      />
                    ) : (
                      (rule.keywords || "")
                        .split(",")
                        .filter((keyword) => keyword.trim().length > 0)
                        .map((keyword) => (
                          <Chip
                            key={keyword}
                            label={keyword.trim()}
                            size="small"
                            style={{ margin: 2 }}
                          />
                        ))
                    )}
                  </TableCell>
                  <TableCell align="center">
                    {rule.steps.map((step, i) => (
                      <Tooltip
                        key={i}
                        title={
                          step.type === "wait_reply"
                            ? `Espera: ${step.expectedReply}`
                            : step.type === "text"
                            ? step.text
                            : step.mediaOriginalName || step.type
                        }
                      >
                        <Chip
                          label={
                            step.type === "wait_reply"
                              ? `${i + 1}. ⏸ Espera`
                              : `${i + 1}. ${i18n.t(
                                  STEP_TYPE_LABEL[step.type] || step.type
                                )}`
                          }
                          size="small"
                          style={{ margin: 2 }}
                        />
                      </Tooltip>
                    ))}
                  </TableCell>
                  <TableCell align="center">
                    <Switch
                      size="small"
                      checked={rule.active}
                      onChange={() => handleToggleActive(rule)}
                      color="primary"
                    />
                  </TableCell>
                  <TableCell align="center">
                    <IconButton
                      size="small"
                      disabled={index === 0 || reordering}
                      onClick={() => handleMoveRule(index, -1)}
                    >
                      <KeyboardArrowUp />
                    </IconButton>
                    <IconButton
                      size="small"
                      disabled={index === rules.length - 1 || reordering}
                      onClick={() => handleMoveRule(index, 1)}
                    >
                      <KeyboardArrowDown />
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={() => handleEditRule(rule)}
                    >
                      <Edit />
                    </IconButton>

                    <IconButton
                      size="small"
                      onClick={() => {
                        setConfirmModalOpen(true);
                        setDeletingRule(rule);
                      }}
                    >
                      <DeleteOutline />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
              {loading && <TableRowSkeleton columns={6} />}
            </>
          </TableBody>
        </Table>
      </Paper>
    </MainContainer>
  );
};


export default AutoReplyFlows;
